import { httpServiceClient } from '@/infra/http'
import { NamingStrategy } from '@/infra/http/http-service-client/http-service-client-types'
import {
  AuthorizationFilterParams,
  AuthorizationListV2Response,
  AuthorizationItem,
  AuthorizationHistoryResponse,
  AuthorizationStatusGroup,
  AuthorizationStatus,
  AuthorizationLabel,
  AuthorizationLabelRelation,
  FilterOptions,
  CourseSkill,
  ApproveRejectPayload,
  EnrollmentAcceptedPayload,
  SuperadminRejectedPayload,
  ChangeStatusPayload,
  AddCommentPayload,
  UpdateLabelsPayload,
  InstitutionPaymentTransferPayload,
  InstitutionPaymentBoletoPayload,
  InstitutionPaymentProofPayload,
  FindAccountByPixResponse,
  SchedulePassPaymentPayload,
  ScheduleTrainingPaymentPayload,
  WrongDocumentEmailPayload,
  InvalidateCertificatePayload,
  LegacyGenericResponse,
  CancelReasonOption,
  CustomFieldOptions
} from './authorization-service-types'
import {
  buildFilterQueryParams,
  createFormDataWithFile,
  formatAuthorizationList,
  formatAuthorizationItem,
  formatAuthorizationHistory,
  formatStatusGroups,
  buildTransferFormData,
  buildBoletoFormData,
  buildPaymentProofFormData,
  throwOnLegacyError,
  formatScholarshipData
} from './authorization-service-utils'
import { makeApiUrl } from '@/main/factories/http'
import { enrollmentStepsService } from '../enrollment/enrollment-steps-service'
import { scholarshipAdminService } from '../scholarship/scholarship-admin-service'

export const createAuthorizationService = (client = httpServiceClient) => {
  const fetchById = async (id: number) => {
    const response = await client<AuthorizationItem>({
      method: 'get',
      url: makeApiUrl(`/enrollment/v2/authorizations/${id}`)
    })
    return formatAuthorizationItem(response.body.data as AuthorizationItem)
  }

  const fetchHistory = async (authId: number) => {
    const response = await client<AuthorizationHistoryResponse>({
      method: 'get',
      url: makeApiUrl(`/authorization_history/${authId}`)
    })
    return formatAuthorizationHistory(response.body.data as AuthorizationHistoryResponse)
  }

  const fetchCourseSkills = async (userId: number, courseId: number) => {
    const response = await client<CourseSkill[]>({
      method: 'get',
      url: makeApiUrl(`/course/get_skills/${userId}/${courseId}`)
    })
    return response.body.data as CourseSkill[]
  }

  const fetchLabels = async () => {
    const response = await client<AuthorizationLabel[]>({
      method: 'get',
      url: makeApiUrl('/authorization_label')
    })
    return response.body.data as AuthorizationLabel[]
  }

  // O catálogo de etiquetas muda raramente. Como o service é singleton, memoizamos
  // a promise para não refazer GET /authorization_label a cada fetchList/detalhe.
  let labelCatalogPromise: Promise<AuthorizationLabel[]> | null = null
  const fetchLabelsCached = () => {
    if (!labelCatalogPromise) {
      labelCatalogPromise = fetchLabels().catch(() => {
        labelCatalogPromise = null // falhou → permite nova tentativa na próxima chamada
        return [] as AuthorizationLabel[]
      })
    }
    return labelCatalogPromise
  }

  // Etiquetas aplicadas a um pedido (mesma rota do legado): retorna só as relações
  // [{ labelId }]; nome/cor são resolvidos contra o catálogo (fetchLabels).
  const fetchLabelsByAuthId = async (id: number) => {
    const response = await client<AuthorizationLabelRelation[]>({
      method: 'get',
      url: makeApiUrl(`/authorization_label/${id}`)
    })
    return response.body.data as AuthorizationLabelRelation[]
  }

  return {
    // ==================== READ OPERATIONS ====================

    async fetchList(params: AuthorizationFilterParams, signal?: AbortSignal) {
      const [response, labelCatalog] = await Promise.all([
        client<AuthorizationListV2Response>({
          method: 'get',
          url: makeApiUrl('/enrollment/v2/authorizations'),
          params: buildFilterQueryParams(params),
          signal
        }),
        fetchLabelsCached()
      ])

      return formatAuthorizationList(
        response.body.data as AuthorizationListV2Response,
        labelCatalog
      )
    },

    fetchById,

    async fetchAuthorizationDetail(authorizationId: number) {
      const historyData = await fetchHistory(authorizationId)
      const [
        authorization,
        skills,
        availableLabels,
        labelRelations,
        requestDetails,
        scholarshipDetails
      ] = await Promise.all([
        fetchById(authorizationId),
        fetchCourseSkills(
          historyData.authorizationInfo?.userId ?? 0,
          historyData.authorizationInfo?.courseId ?? 0
        ).catch(() => []),
        fetchLabelsCached(),
        fetchLabelsByAuthId(authorizationId).catch(() => []),
        enrollmentStepsService
          .fetchEnrollmentStepsDetail(String(authorizationId))
          .catch(() => null),
        scholarshipAdminService.fetchScholarships(authorizationId).catch(() => null)
      ])

      // Resolve as etiquetas aplicadas (só têm labelId) contra o catálogo (nome/cor).
      const appliedLabels = labelRelations
        .map(relation => availableLabels.find(label => label.id === relation.labelId))
        .filter((label): label is AuthorizationLabel => Boolean(label))

      // `payableAuthorization` e `individualPaymentEnabled` vêm do detalhe v2 (spread de
      // `authorization` por último), não do endpoint legado de histórico.
      return {
        authorizationDetail: {
          ...historyData.authorizationInfo,
          ...authorization,
          labels: appliedLabels,
          authorizationHistoryList: historyData.authorizationHistoryList,
          rating: historyData.rating,
          skills
        },
        availableLabels,
        requestDetails,
        scholarshipDetails: formatScholarshipData(scholarshipDetails)
      }
    },

    fetchHistory,

    async fetchStatusGroups() {
      const response = await client<AuthorizationStatusGroup[]>({
        method: 'get',
        url: makeApiUrl('/authorization_status/groups')
      })

      return formatStatusGroups(response.body.data as unknown as Array<{ [key: string]: unknown }>)
    },

    async fetchAllStatuses() {
      const response = await client<AuthorizationStatus[]>({
        method: 'get',
        url: makeApiUrl('/authorization_status')
      })

      return response.body.data as AuthorizationStatus[]
    },

    // Motivos de cancelamento válidos (IDs da tabela authorization_cancel_reason),
    // já filtrados por papel e localizados pelo backend.
    async fetchCancelReasons(authId: number) {
      const response = await client<{ reasons: CancelReasonOption[] }>({
        method: 'get',
        url: makeApiUrl(`/v2/user/enrollments/${authId}/cancel/reasons`)
      })

      return (response.body?.data as { reasons?: CancelReasonOption[] })?.reasons ?? []
    },

    fetchLabels,

    fetchLabelsByAuthId,

    async fetchFilterOptions() {
      const response = await client<FilterOptions>({
        method: 'get',
        url: makeApiUrl('/user/statusOptions')
      })

      return response.body.data as FilterOptions
    },

    // Cargos/Departamentos vêm dos campos customizados (GET /management/options),
    // mesma fonte da tela de Colaboradores. Duplicado aqui de propósito para não
    // acoplar o loader de Pedidos ao collaboratorService.
    async fetchCustomFieldOptions(): Promise<CustomFieldOptions> {
      const response = await client<CustomFieldOptions>({
        method: 'get',
        url: makeApiUrl('/management/options')
      })

      const data = response.body?.data as Partial<CustomFieldOptions> | undefined
      return {
        departments: data?.departments ?? [],
        jobs: data?.jobs ?? []
      }
    },

    fetchCourseSkills,

    async checkRefundInPlan(id: number) {
      const response = await client<boolean>({
        method: 'get',
        url: makeApiUrl(`/authorization-refund/${id}/in-user-plan`)
      })

      return response.body.data as boolean
    },

    async fetchCompanySharedBudget() {
      // Backend: GET /api/budget/company-shared-budget → SharedBudgetResponseDTO
      // { budget: { balance, ... } }. Como o DTO não tem chave `data` no topo, o
      // httpServiceClient embrulha o corpo em { data: <corpo> } — daí o `.data.budget`.
      const response = await client<{ budget?: { balance?: number } }>({
        method: 'get',
        url: makeApiUrl('/budget/company-shared-budget')
      })

      return (response.body?.data as { budget?: { balance?: number } })?.budget?.balance ?? 0
    },

    // ==================== WRITE OPERATIONS ====================

    // v2: aceite do líder migrado de PUT /authorization/authorization_accepted
    // para PUT /enrollment/v2/authorizations/{id}/acceptance (TS-8602). O id vai na
    // URL; comment/submitFromEmail/file seguem como multipart (nomes = @RequestParam v2).
    async approveAuthorization(payload: ApproveRejectPayload) {
      const { id, ...formFields } = payload
      await client<void>({
        method: 'put',
        url: makeApiUrl(`/enrollment/v2/authorizations/${id}/acceptance`),
        body: createFormDataWithFile(formFields as unknown as Record<string, unknown>)
      })
    },

    // v2: recusa do líder migrada de PUT /authorization/authorization_rejected
    // para PUT /enrollment/v2/authorizations/{id}/rejection (TS-8606). O id vai na
    // URL; comment/submitFromEmail/file seguem como multipart (nomes = @RequestParam v2).
    async rejectAuthorization(payload: ApproveRejectPayload) {
      const { id, ...formFields } = payload
      await client<void>({
        method: 'put',
        url: makeApiUrl(`/enrollment/v2/authorizations/${id}/rejection`),
        body: createFormDataWithFile(formFields as unknown as Record<string, unknown>)
      })
    },

    async enrollmentAccepted(payload: EnrollmentAcceptedPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization/enrollment_accepted'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async superadminRejected(payload: SuperadminRejectedPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization/superadmin_rejected'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async enrollmentConfirmation(authId: number, confirmation: boolean) {
      await client<void>({
        method: 'put',
        url: makeApiUrl('/authorization/enrollment_confirmation'),
        body: { authId, confirmation }
      })
    },

    async changeStatus(payload: ChangeStatusPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl(`/v2/admin/authorization/${payload.id}/status`),
        body: { statusId: payload.statusId, comment: payload.comment },
        bodyNamingStrategy: NamingStrategy.camelCase
      })
    },

    async addComment(payload: AddCommentPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_history'),
        body: createFormDataWithFile(payload as unknown as Record<string, unknown>)
      })
    },

    async updateLabels(payload: UpdateLabelsPayload) {
      await client<void>({
        method: 'put',
        url: makeApiUrl(`/enrollment/v2/authorizations/${payload.id}/labels`),
        body: { labelsId: payload.labelsId }
      })
    },

    async submitBoleto(data: FormData) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/submit_boleto'),
        body: data
      })
    },

    // SkillPass payment — legacy institution_payment/* (multipart + receipts).
    // Stark Bank: transfer (dados padrão / PIX resolvido / TED) e boleto.
    async institutionPaymentTransfer(payload: InstitutionPaymentTransferPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/institution_payment/transfer'),
        body: buildTransferFormData(payload)
      })
    },

    async institutionPaymentBoleto(payload: InstitutionPaymentBoletoPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/institution_payment/boleto'),
        body: buildBoletoFormData(payload)
      })
    },

    // "Por outra plataforma": comprovante manual de pagamento.
    async institutionPaymentProof(payload: InstitutionPaymentProofPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/institution_payment/payment_proof'),
        body: buildPaymentProofFormData(payload)
      })
    },

    // Resolve a chave PIX em dados bancários (legado: GET /refund/find-pix).
    async findAccountByPix(pix: string) {
      const response = await client<FindAccountByPixResponse>({
        method: 'get',
        url: makeApiUrl('/refund/find-pix'),
        params: { pix }
      })
      return response.body.data
    },

    async resendEmail(authId: number) {
      await client<void>({
        method: 'post',
        url: makeApiUrl(`/enrollment/v2/authorizations/${authId}/email-resend`)
      })
    },

    // ==================== SKILLPASS PAYMENT ====================

    async checkPaymentScheduled(authId: number) {
      try {
        await client<void>({
          method: 'get',
          url: makeApiUrl(`/authorization_pass_payment/${authId}`)
        })
        return true
      } catch {
        return false
      }
    },

    async schedulePassPayment(payload: SchedulePassPaymentPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_pass_payment'),
        body: payload
      })
    },

    // Cancela o pagamento SkillPass agendado (legado: DELETE
    // /authorization_institution_payment/{id}).
    async cancelPassPayment(authId: number) {
      await client<void>({
        method: 'delete',
        url: makeApiUrl(`/authorization_institution_payment/${authId}`)
      })
    },

    // ==================== TRAINING PAYMENT & COMPLIANCE ====================

    async scheduleTrainingPayment(payload: ScheduleTrainingPaymentPayload) {
      await client<void>({
        method: 'post',
        url: makeApiUrl('/authorization_training_payment'),
        body: payload
      })
    },

    async cancelTrainingPayment(authId: number) {
      await client<void>({
        method: 'delete',
        url: makeApiUrl(`/authorization_training_payment/${authId}`)
      })
    },

    async deletePaymentProof(authId: number) {
      await client<void>({
        method: 'delete',
        url: makeApiUrl(`/authorization_payment_proof/${authId}`)
      })
    },

    // ==================== EMAIL & REFUND ====================

    async sendWrongDocumentEmail(payload: WrongDocumentEmailPayload) {
      const response = await client<LegacyGenericResponse>({
        method: 'post',
        url: makeApiUrl('/authorization/wrong-document'),
        params: { authId: payload.authId, customMessage: payload.customMessage }
      })
      throwOnLegacyError(response.body?.data)
    },

    async validateCertificate(authId: number) {
      const response = await client<LegacyGenericResponse>({
        method: 'post',
        url: makeApiUrl(`/conclusion-certificate/validate/${authId}`)
      })
      throwOnLegacyError(response.body?.data)
    },

    async invalidateCertificate(payload: InvalidateCertificatePayload) {
      const response = await client<LegacyGenericResponse>({
        method: 'post',
        url: makeApiUrl(`/conclusion-certificate/invalidate/${payload.authId}`),
        body: { reasonId: payload.reasonId, comment: payload.comment }
      })
      throwOnLegacyError(response.body?.data)
    },

    async cancelRefund(authId: number) {
      await client<void>({
        method: 'post',
        url: makeApiUrl(`/refund/cancel/${authId}`)
      })
    }
  }
}

export const authorizationService = createAuthorizationService()
